// src/pages/MinhasOrdens.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './MinhasOrdens.css';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaSearch,
  FaFire,
  FaTrophy,
  FaBullseye,
  FaLayerGroup,
} from 'react-icons/fa';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// ─────────────────────────────────────────────
// Helpers de data
// ─────────────────────────────────────────────
const toISODate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createDateAtNoon = (dateLike) => {
  if (!dateLike) return new Date();
  const d = typeof dateLike === 'string'
    ? new Date(`${dateLike}T12:00:00`)
    : new Date(dateLike);
  return d;
};

const startOfWeek = (date) => {
  const d = createDateAtNoon(date);
  const day = d.getDay(); // 0 dom, 1 seg...
  const diff = day === 0 ? -6 : 1 - day; // semana começando na segunda
  d.setDate(d.getDate() + diff);
  d.setHours(12, 0, 0, 0);
  return d;
};

const endOfWeek = (date) => {
  const start = startOfWeek(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(12, 0, 0, 0);
  return end;
};

const getRangeByPreset = (preset, baseDate = new Date()) => {
  const d = createDateAtNoon(baseDate);
  let start = new Date(d);
  let end = new Date(d);

  switch (preset) {
    case 'semana': {
      start = startOfWeek(d);
      end = endOfWeek(d);
      break;
    }

    case 'mes': {
      start = new Date(d.getFullYear(), d.getMonth(), 1, 12);
      end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 12);
      break;
    }

    case 'trimestre': {
      const quarterStartMonth = Math.floor(d.getMonth() / 3) * 3;
      start = new Date(d.getFullYear(), quarterStartMonth, 1, 12);
      end = new Date(d.getFullYear(), quarterStartMonth + 3, 0, 12);
      break;
    }

    case 'semestre': {
      const semesterStartMonth = d.getMonth() < 6 ? 0 : 6;
      start = new Date(d.getFullYear(), semesterStartMonth, 1, 12);
      end = new Date(d.getFullYear(), semesterStartMonth + 6, 0, 12);
      break;
    }

    case 'ano': {
      start = new Date(d.getFullYear(), 0, 1, 12);
      end = new Date(d.getFullYear(), 11, 31, 12);
      break;
    }

    case 'personalizado':
    default: {
      start = new Date(d.getFullYear(), d.getMonth(), 1, 12);
      end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 12);
      break;
    }
  }

  return {
    preset,
    primeiroDia: toISODate(start),
    ultimoDia: toISODate(end),
  };
};

const fmtDate = (iso) =>
  iso
    ? createDateAtNoon(iso).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
      })
    : '—';

const fmtFullDate = (iso) =>
  iso
    ? createDateAtNoon(iso).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

const fmtMonthShort = (date) =>
  createDateAtNoon(date).toLocaleDateString('pt-BR', {
    month: 'short',
  });

const normalizeRange = (range) => {
  if (!range?.primeiroDia || !range?.ultimoDia) return range;
  if (range.primeiroDia <= range.ultimoDia) return range;

  return {
    ...range,
    primeiroDia: range.ultimoDia,
    ultimoDia: range.primeiroDia,
  };
};

const getPresetLabel = (preset) => {
  const labels = {
    semana: 'Semana',
    mes: 'Mês',
    trimestre: 'Trimestre',
    semestre: 'Semestre',
    ano: 'Ano',
    personalizado: 'Personalizado',
  };
  return labels[preset] || 'Período';
};

// ─────────────────────────────────────────────
// Heatmap helpers
// ─────────────────────────────────────────────
const parseDayMonthLabel = (label, fallbackYear = new Date().getFullYear()) => {
  if (!label || typeof label !== 'string') return null;

  // tenta dd/mm
  const parts = label.split('/');
  if (parts.length >= 2) {
    const dd = Number(parts[0]);
    const mm = Number(parts[1]);
    if (!Number.isNaN(dd) && !Number.isNaN(mm)) {
      return new Date(fallbackYear, mm - 1, dd, 12);
    }
  }

  return null;
};

const buildHeatmap = (dadosGrafico) => {
  const fallbackYear = new Date().getFullYear();
  const map = {};

  (dadosGrafico || []).forEach((d) => {
    if (!d?.dia) return;
    map[d.dia] = (d.criadas || 0) + (d.concluidas || 0);
  });

  const today = createDateAtNoon(new Date());
  const start = new Date(today);
  start.setDate(today.getDate() - 83); // 12 semanas = 84 dias
  start.setHours(12, 0, 0, 0);

  const weeks = [];
  let cursor = new Date(start);

  for (let w = 0; w < 12; w++) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const cellDate = new Date(cursor);
      const key = cellDate.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
      });

      week.push({
        iso: toISODate(cellDate),
        label: key,
        monthLabel: fmtMonthShort(cellDate).replace('.', ''),
        val: map[key] || 0,
        date: new Date(cellDate),
        fallbackYear,
      });

      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }

  const monthHeaders = weeks.map((week, index) => {
    const firstDay = week[0]?.date;
    const showLabel =
      index === 0 ||
      fmtMonthShort(weeks[index - 1][0]?.date).toLowerCase() !==
        fmtMonthShort(firstDay).toLowerCase();

    return showLabel
      ? fmtMonthShort(firstDay).replace('.', '')
      : '';
  });

  return { weeks, monthHeaders };
};

const calcStreak = (dadosGrafico) => {
  if (!dadosGrafico?.length) return 0;

  const active = new Set(
    dadosGrafico
      .filter((d) => (d.criadas || 0) + (d.concluidas || 0) > 0)
      .map((d) => d.dia)
  );

  let streak = 0;
  const today = createDateAtNoon(new Date());

  for (let i = 0; i <= 60; i++) {
    const dt = new Date(today);
    dt.setDate(today.getDate() - i);

    const key = dt.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    });

    if (active.has(key)) {
      streak++;
    } else if (i > 0) {
      break;
    }
  }

  return streak;
};

// ─────────────────────────────────────────────
// Tooltip do gráfico
// ─────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="mo-tooltip">
      <span className="mo-tooltip-label">{label}</span>
      <span className="mo-tooltip-criadas">
        Criadas: {payload[0]?.value ?? 0}
      </span>
      <span className="mo-tooltip-concluidas">
        Concluídas: {payload[1]?.value ?? 0}
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────
// Heatmap de atividade
// ─────────────────────────────────────────────
const ActivityHeatmap = ({ dadosGrafico }) => {
  const { weeks, monthHeaders } = useMemo(() => buildHeatmap(dadosGrafico), [dadosGrafico]);

  const allCells = weeks.flat();
  const max = useMemo(
    () => Math.max(...allCells.map((c) => c.val), 1),
    [allCells]
  );

  const intensity = (val) => {
    if (!val) return 0;
    const pct = val / max;
    if (pct < 0.25) return 1;
    if (pct < 0.5) return 2;
    if (pct < 0.75) return 3;
    return 4;
  };

  const dayLabels = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D']; // Seg -> Dom

  return (
    <div className="mo-heatmap-shell">
      <div className="mo-heatmap-months">
        <span className="mo-heatmap-months-spacer" />
        {monthHeaders.map((m, i) => (
          <span key={`${m}-${i}`} className="mo-heatmap-month-label">
            {m}
          </span>
        ))}
      </div>

      <div className="mo-heatmap-body">
        <div className="mo-heatmap-days">
          {dayLabels.map((day, i) => (
            <span key={`${day}-${i}`} className="mo-heatmap-day-label">
              {day}
            </span>
          ))}
        </div>

        <div className="mo-heatmap-grid">
          {weeks.map((week, wi) => (
            <div key={wi} className="mo-heatmap-col">
              {week.map((cell) => (
                <div
                  key={cell.iso}
                  className={`mo-heatmap-cell mo-heatmap-cell--${intensity(cell.val)}`}
                  title={`${cell.label} · ${cell.val} atividade${cell.val !== 1 ? 's' : ''}`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="mo-heatmap-legend">
        <span>Menos</span>
        {[0, 1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={`mo-heatmap-cell mo-heatmap-cell--${level}`}
          />
        ))}
        <span>Mais</span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Principal
// ─────────────────────────────────────────────
const PERIOD_OPTIONS = [
  { key: 'semana', label: 'Semana', hint: 'Últimos 7 dias úteis do calendário atual' },
  { key: 'mes', label: 'Mês', hint: 'Visão mensal atual' },
  { key: 'trimestre', label: 'Trimestre', hint: '3 meses corridos' },
  { key: 'semestre', label: 'Semestre', hint: '6 meses corridos' },
  { key: 'ano', label: 'Ano', hint: 'Ano civil atual' },
  { key: 'personalizado', label: 'Personalizado', hint: 'Escolha manualmente' },
];

export default function MinhasOrdens({ user }) {
  const navigate = useNavigate();

  const initialRange = useMemo(() => getRangeByPreset('mes'), []);

  const [stats, setStats] = useState({
    ordensCriadas: 0,
    ordensConcluidas: 0,
  });

  const [listaCriadas, setListaCriadas] = useState([]);
  const [listaConcluidas, setListaConcluidas] = useState([]);
  const [dadosGrafico, setDadosGrafico] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filtro, setFiltro] = useState(initialRange);
  const [periodo, setPeriodo] = useState(initialRange);

  const [activeTab, setActiveTab] = useState('criadas'); // 'criadas' | 'concluidas'

  const streak = useMemo(() => calcStreak(dadosGrafico), [dadosGrafico]);

  const taxaConc = useMemo(() => {
    if (!stats.ordensCriadas) return 0;
    return Math.round((stats.ordensConcluidas / stats.ordensCriadas) * 100);
  }, [stats]);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          dataInicio: periodo.primeiroDia,
          dataFim: periodo.ultimoDia,
        },
      };

      const [prodRes, rankRes] = await Promise.all([
        api.get('/api/ordens/produtividade/usuario', config),
        api.get('/api/ordens/produtividade/ranking', config).catch(() => ({ data: [] })),
      ]);

      setStats({
        ordensCriadas: prodRes.data.ordensCriadas || 0,
        ordensConcluidas: prodRes.data.ordensConcluidas || 0,
      });

      setDadosGrafico(prodRes.data.dadosGrafico || []);
      setListaCriadas(prodRes.data.listaCriadas?.slice(0, 8) || []);
      setListaConcluidas(prodRes.data.listaConcluidas?.slice(0, 8) || []);
      setRanking(Array.isArray(rankRes.data) ? rankRes.data.slice(0, 5) : []);
    } catch (e) {
      console.error('MinhasOrdens:', e);
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePeriodoChange = (e) => {
    const { name, value } = e.target;
    setFiltro((prev) => ({
      ...prev,
      preset: 'personalizado',
      [name]: value,
    }));
  };

  const handlePresetSelect = (presetKey) => {
    if (presetKey === 'personalizado') {
      setFiltro((prev) => ({ ...prev, preset: 'personalizado' }));
      return;
    }

    const nextRange = getRangeByPreset(presetKey);
    setFiltro(nextRange);
    setPeriodo(nextRange);
  };

  const handleAplicarFiltro = () => {
    const normalized = normalizeRange(filtro);
    setFiltro(normalized);
    setPeriodo(normalized);
  };

  const periodoLabel = `${fmtDate(periodo.primeiroDia)} – ${fmtDate(periodo.ultimoDia)}`;
  const periodoHeaderLabel = `${fmtFullDate(periodo.primeiroDia)} até ${fmtFullDate(periodo.ultimoDia)}`;
  const listaAtiva = activeTab === 'criadas' ? listaCriadas : listaConcluidas;

  const weekData = useMemo(() => {
    const dias = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const acc = Array(7)
      .fill(0)
      .map((_, i) => ({ dia: dias[i], val: 0 }));

    const fallbackYear = createDateAtNoon(periodo.primeiroDia).getFullYear();

    (dadosGrafico || []).forEach((d) => {
      const parsed = parseDayMonthLabel(d.dia, fallbackYear);
      if (!parsed) return;

      const dow = parsed.getDay();
      acc[dow].val += (d.criadas || 0) + (d.concluidas || 0);
    });

    return acc;
  }, [dadosGrafico, periodo]);

  const weekMax = Math.max(...weekData.map((d) => d.val), 1);

  return (
    <div className="mo-root">
      {/* Header */}
      <header className="mo-header">
        <button
          className="mo-back"
          onClick={() => navigate(-1)}
          aria-label="Voltar"
        >
          <FaArrowLeft size={14} />
        </button>

        <div className="mo-header-text">
          <h1 className="mo-title">Minha produtividade</h1>
          <p className="mo-subtitle">
            {user?.nome_empresa || user?.empresa || 'Sua empresa'} · {periodoLabel}
          </p>
        </div>
      </header>

      {/* Filtro Premium */}
      <section className="mo-filter-card">
        <div className="mo-filter-top">
          <div className="mo-filter-top-left">
            <div className="mo-filter-badge">
              <FaLayerGroup size={12} />
              <span>Filtro inteligente</span>
            </div>

            <div>
              <h2 className="mo-filter-title">Selecione a janela de análise</h2>
              <p className="mo-filter-subtitle">
                Alterne rapidamente entre visões semanais, mensais, trimestrais,
                semestrais, anuais ou personalize manualmente.
              </p>
            </div>
          </div>

          <div className="mo-filter-summary">
            <span className="mo-filter-summary-label">Período ativo</span>
            <strong className="mo-filter-summary-value">
              {getPresetLabel(periodo.preset)}
            </strong>
            <span className="mo-filter-summary-range">{periodoHeaderLabel}</span>
          </div>
        </div>

        <div className="mo-filter-content">
          <div className="mo-period-matrix">
            {PERIOD_OPTIONS.map((option) => {
              const active = filtro.preset === option.key;

              return (
                <button
                  key={option.key}
                  type="button"
                  className={`mo-period-option ${active ? 'mo-period-option--active' : ''}`}
                  onClick={() => handlePresetSelect(option.key)}
                >
                  <span className="mo-period-option-title">{option.label}</span>
                  <span className="mo-period-option-hint">{option.hint}</span>
                </button>
              );
            })}
          </div>

          <div className="mo-filter-bottom">
            <div className="mo-date-grid">
              <div className="mo-filter-group">
                <label htmlFor="primeiroDia">
                  <FaCalendarAlt size={11} />
                  Início
                </label>
                <div className="mo-input-wrap">
                  <input
                    type="date"
                    id="primeiroDia"
                    name="primeiroDia"
                    className="mo-input"
                    value={filtro.primeiroDia}
                    onChange={handlePeriodoChange}
                  />
                </div>
              </div>

              <div className="mo-filter-group">
                <label htmlFor="ultimoDia">
                  <FaCalendarAlt size={11} />
                  Fim
                </label>
                <div className="mo-input-wrap">
                  <input
                    type="date"
                    id="ultimoDia"
                    name="ultimoDia"
                    className="mo-input"
                    value={filtro.ultimoDia}
                    onChange={handlePeriodoChange}
                  />
                </div>
              </div>
            </div>

            <div className="mo-filter-actions">
              <div className="mo-range-chip">
                <span className="mo-range-chip-label">Seleção</span>
                <strong className="mo-range-chip-value">
                  {fmtDate(filtro.primeiroDia)} – {fmtDate(filtro.ultimoDia)}
                </strong>
              </div>

              <button
                className="mo-btn-primary"
                onClick={handleAplicarFiltro}
                disabled={loading}
              >
                <FaSearch size={12} />
                {loading ? 'Atualizando...' : 'Aplicar filtro'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* KPIs */}
      <div className="mo-kpi-grid">
        <div className="mo-kpi mo-kpi--blue">
          <div className="mo-kpi-icon">
            <i className="ti ti-circle-plus" />
          </div>
          <div className="mo-kpi-body">
            <span className="mo-kpi-value">{loading ? '—' : stats.ordensCriadas}</span>
            <span className="mo-kpi-label">OS criadas</span>
          </div>
        </div>

        <div className="mo-kpi mo-kpi--green">
          <div className="mo-kpi-icon">
            <i className="ti ti-circle-check" />
          </div>
          <div className="mo-kpi-body">
            <span className="mo-kpi-value">{loading ? '—' : stats.ordensConcluidas}</span>
            <span className="mo-kpi-label">OS concluídas</span>
          </div>
        </div>

        <div className="mo-kpi mo-kpi--amber">
          <div className="mo-kpi-icon">
            <FaBullseye size={20} />
          </div>
          <div className="mo-kpi-body">
            <span className="mo-kpi-value">{loading ? '—' : `${taxaConc}%`}</span>
            <span className="mo-kpi-label">Taxa de conclusão</span>
          </div>
          <div className="mo-kpi-bar-wrap">
            <div className="mo-kpi-bar" style={{ width: `${taxaConc}%` }} />
          </div>
        </div>

        <div className={`mo-kpi mo-kpi--streak ${streak >= 3 ? 'mo-kpi--streak-hot' : ''}`}>
          <div className="mo-kpi-icon">
            <FaFire size={20} />
          </div>
          <div className="mo-kpi-body">
            <span className="mo-kpi-value">{loading ? '—' : streak}</span>
            <span className="mo-kpi-label">
              {streak >= 3 ? '🔥 dias seguidos' : 'dias ativos seguidos'}
            </span>
          </div>

          {streak >= 5 && <div className="mo-streak-badge">Em chamas!</div>}
        </div>
      </div>

      {/* Gráfico e atividade semanal */}
      <div className="mo-two-col">
        <div className="mo-card mo-card--chart">
          <div className="mo-card-header">
            <span className="mo-card-title">Desempenho diário</span>

            <div className="mo-legend">
              <span className="mo-legend-item mo-legend-item--blue">Criadas</span>
              <span className="mo-legend-item mo-legend-item--green">Concluídas</span>
            </div>
          </div>

          {loading ? (
            <div className="mo-skeleton" style={{ height: 240 }} />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart
                data={dadosGrafico}
                margin={{ top: 10, right: 8, left: -28, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#378add" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#378add" stopOpacity={0} />
                  </linearGradient>

                  <linearGradient id="gk" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1d9e75" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#1d9e75" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e2e8f0"
                  vertical={false}
                />

                <XAxis
                  dataKey="dia"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  dy={6}
                />

                <YAxis
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                />

                <Tooltip content={<CustomTooltip />} />

                <Area
                  type="monotone"
                  dataKey="criadas"
                  name="Criadas"
                  stroke="#378add"
                  strokeWidth={2}
                  fill="url(#gc)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />

                <Area
                  type="monotone"
                  dataKey="concluidas"
                  name="Concluídas"
                  stroke="#1d9e75"
                  strokeWidth={2}
                  fill="url(#gk)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="mo-card mo-card--week">
          <div className="mo-card-header">
            <span className="mo-card-title">Atividade por dia da semana</span>
          </div>

          {loading ? (
            <div className="mo-skeleton" style={{ height: 240 }} />
          ) : (
            <div className="mo-week-bars">
              {weekData.map((d) => (
                <div key={d.dia} className="mo-week-bar-col">
                  <span className="mo-week-bar-val-top">{d.val}</span>

                  <div className="mo-week-bar-track">
                    <div
                      className="mo-week-bar-fill"
                      style={{
                        height: `${Math.max(8, Math.round((d.val / weekMax) * 100))}%`,
                      }}
                    />
                  </div>

                  <span className="mo-week-bar-label">{d.dia}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Heatmap */}
      <div className="mo-card">
        <div className="mo-card-header">
          <span className="mo-card-title">Mapa de atividade — últimas 12 semanas</span>

          {streak > 0 && (
            <span className="mo-streak-pill">
              <FaFire size={11} /> {streak} {streak === 1 ? 'dia' : 'dias'}
            </span>
          )}
        </div>

        {loading ? (
          <div className="mo-skeleton" style={{ height: 130 }} />
        ) : (
          <ActivityHeatmap dadosGrafico={dadosGrafico} />
        )}
      </div>

      {/* Lista + Ranking */}
      <div className="mo-two-col">
        <div className="mo-card">
          <div className="mo-card-header">
            <span className="mo-card-title">Minhas ordens</span>

            <div className="mo-tabs">
              <button
                className={`mo-tab ${activeTab === 'criadas' ? 'mo-tab--active' : ''}`}
                onClick={() => setActiveTab('criadas')}
              >
                Criadas
                <span className="mo-tab-count">{stats.ordensCriadas}</span>
              </button>

              <button
                className={`mo-tab ${activeTab === 'concluidas' ? 'mo-tab--active' : ''}`}
                onClick={() => setActiveTab('concluidas')}
              >
                Concluídas
                <span className="mo-tab-count">{stats.ordensConcluidas}</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="mo-skeleton" style={{ height: 180 }} />
          ) : listaAtiva.length > 0 ? (
            <div className="mo-os-list">
              {listaAtiva.map((os) => (
                <div key={os.id} className="mo-os-item">
                  <span className="mo-os-id">#{os.id}</span>

                  <div className="mo-os-info">
                    <span className="mo-os-cliente">
                      {os.nome_cliente || 'Cliente'}
                    </span>
                    <span className="mo-os-local">
                      {os.local_atendimento || 'Local não informado'}
                    </span>
                  </div>

                  <span className="mo-os-date">
                    {os.data_criacao
                      ? new Date(os.data_criacao).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                        })
                      : '—'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="mo-empty">Nenhuma OS no período</div>
          )}
        </div>

        <div className="mo-card">
          <div className="mo-card-header">
            <span className="mo-card-title">Ranking da equipe</span>
            <FaTrophy size={14} style={{ color: '#ba7517' }} />
          </div>

          {loading ? (
            <div className="mo-skeleton" style={{ height: 180 }} />
          ) : ranking.length > 0 ? (
            <div className="mo-ranking-list">
              {ranking.map((r, i) => {
                const medals = ['🥇', '🥈', '🥉'];
                const initials = (r.nome || '?')
                  .split(' ')
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase();

                const isMe = r.nome === user?.nome;

                return (
                  <div
                    key={r.id || `${r.nome}-${i}`}
                    className={`mo-ranking-item ${isMe ? 'mo-ranking-item--me' : ''}`}
                  >
                    <span className="mo-ranking-pos">
                      {medals[i] ?? `${i + 1}º`}
                    </span>

                    <div className="mo-ranking-avatar">{initials}</div>

                    <span className="mo-ranking-name">
                      {r.nome}
                      {isMe ? ' (você)' : ''}
                    </span>

                    <span className="mo-ranking-count">{r.concluidas} OS</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mo-empty">Sem dados de ranking no período</div>
          )}
        </div>
      </div>
    </div>
  );
}